import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function AdmissionsPage() {
  const [batches, setBatches] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name:"", email:"", phone:"", gender:"M", dob:"",
    parent_name:"", parent_phone:"", address:"",
    prev_school:"", prev_marks:"",
    batch_id:"", scholarship:0,
  });

  useEffect(()=>{ api.get("/batches").then(({data})=>setBatches(data)); },[]);

  const next = () => setStep((s) => Math.min(4, s+1));
  const back = () => setStep((s) => Math.max(1, s-1));

  const submit = async () => {
    const batch = batches.find(b => b.id === form.batch_id);
    const roll = `CE${Date.now()}`.slice(-9);
    await api.post("/students", {
      ...form, roll_no: roll,
      batch_name: batch?.name, course_id: batch?.course_id, course_name: batch?.course_name,
      admission_date: new Date().toISOString().slice(0,10),
      status:"active",
    });
    // Also generate fee
    if (batch) {
      const fee = batch?.course_name ? 0 : 0;
    }
    toast.success("Admission complete");
    setStep(4);
  };

  const steps = ["Personal", "Parent & Address", "Academics", "Batch"];

  return (
    <div>
      <PageHeader title="Admissions" subtitle="Guided admission workflow" />

      <div className="linkedin-card p-5">
        <div className="flex items-center mb-6">
          {steps.map((s, i) => {
            const idx = i+1;
            const active = step >= idx;
            return (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2" data-testid={`admission-step-${idx}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${active ? 'bg-[#0A66C2] text-white' : 'bg-[#EDEBE7] text-[#00000066]'}`}>
                    {step > idx ? <CheckCircle2 size={16}/> : idx}
                  </div>
                  <span className={`text-sm font-medium ${active ? 'text-[#000000E6]' : 'text-[#00000066]'}`}>{s}</span>
                </div>
                {i < steps.length-1 && <div className={`flex-1 h-[2px] mx-3 ${step > idx ? 'bg-[#0A66C2]' : 'bg-[#E0DFDC]'}`}/>}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Full name" data-testid="adm-name-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
            <input placeholder="Email" data-testid="adm-email-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
            <input placeholder="Phone" data-testid="adm-phone-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
            <input type="date" placeholder="DOB" data-testid="adm-dob-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.dob} onChange={e=>setForm({...form, dob:e.target.value})}/>
            <select data-testid="adm-gender-select" className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.gender} onChange={e=>setForm({...form, gender:e.target.value})}>
              <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
            </select>
          </div>
        )}
        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Parent name" data-testid="adm-parent-name-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.parent_name} onChange={e=>setForm({...form, parent_name:e.target.value})}/>
            <input placeholder="Parent phone" data-testid="adm-parent-phone-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.parent_phone} onChange={e=>setForm({...form, parent_phone:e.target.value})}/>
            <textarea placeholder="Address" data-testid="adm-address-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md col-span-2 min-h-[80px]" value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/>
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Previous school/college" data-testid="adm-prev-school-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.prev_school} onChange={e=>setForm({...form, prev_school:e.target.value})}/>
            <input placeholder="Previous marks / percentage" data-testid="adm-prev-marks-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.prev_marks} onChange={e=>setForm({...form, prev_marks:e.target.value})}/>
          </div>
        )}
        {step === 4 && (
          <div className="text-center py-8" data-testid="admission-complete">
            <div className="w-16 h-16 rounded-full bg-[#D1E7DD] text-[#057642] flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={32}/></div>
            <div className="text-xl font-semibold">Admission complete</div>
            <div className="text-sm text-[#00000099] mt-1">{form.name} has been enrolled. Redirect to Students to see the record.</div>
          </div>
        )}

        {step < 4 && step === 4-1 && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <select data-testid="adm-batch-select" className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.batch_id} onChange={e=>setForm({...form, batch_id:e.target.value})}>
              <option value="">Select batch</option>
              {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <input type="number" placeholder="Scholarship / discount %" data-testid="adm-scholarship-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.scholarship} onChange={e=>setForm({...form, scholarship:e.target.value})}/>
          </div>
        )}

        <div className="flex justify-between mt-6 pt-4 border-t border-[#E0DFDC]">
          <button onClick={back} disabled={step<=1} data-testid="admission-back-btn" className="px-5 py-2 rounded-full border border-[#00000033] text-[#000000E6] font-semibold disabled:opacity-40">Back</button>
          {step < 3 && <button onClick={next} data-testid="admission-next-btn" className="btn-primary">Next</button>}
          {step === 3 && <button onClick={submit} data-testid="admission-submit-btn" className="btn-primary">Complete admission</button>}
          {step === 4 && <button onClick={()=>{setStep(1); setForm({...form, name:"", email:"", phone:""})}} data-testid="admission-new-btn" className="btn-primary">New admission</button>}
        </div>
      </div>
    </div>
  );
}
